import { http } from '@inertiajs/react';
import type { AxiosError } from 'axios';

import {
  DELETION_CONSTRAINT_ERROR_EVENT,
  type DeletionConstraintErrorDetail
} from '@/constants/deletionConstraintError';
import { VALIDATION_ERROR_KEYS } from '@/constants/validationErrorKeys';

import { parseJson } from './parseJson';

const VALIDATION_STATUS = 422;

export interface ValidationErrorResponseBody {
  errors?: Record<string, string | string[]>;
}

/**
 * Dispatches {@link DELETION_CONSTRAINT_ERROR_EVENT} when `body` is a validation response carrying
 * `errors.deletion_constraints`. Returns whether the event was dispatched.
 */
export function dispatchDeletionConstraintError(
  status: number | undefined,
  body: unknown
): boolean {
  if (status !== VALIDATION_STATUS) {
    return false;
  }

  const payloadJson = extractPayloadJson(body);

  if (payloadJson === null) {
    return false;
  }

  window.dispatchEvent(
    new CustomEvent<DeletionConstraintErrorDetail>(DELETION_CONSTRAINT_ERROR_EVENT, {
      detail: { payloadJson }
    })
  );

  return true;
}

/** Rejection handler for an axios response interceptor. */
export function handleDeletionConstraintAxiosError(
  error: AxiosError<ValidationErrorResponseBody>
): void {
  dispatchDeletionConstraintError(error.response?.status, error.response?.data);
}

/**
 * Inertia's `useHttp` uses its own HTTP client rather than axios, and its failures are not router
 * visits, so neither the axios interceptor nor `page.props.errors` ever sees them. `http.onError`
 * is the one hook that fires for every Inertia HTTP client request.
 */
export function registerDeletionConstraintHttpErrorHandler(): () => void {
  return http.onError((error) => {
    const response = (error as { response?: { status: number; data: unknown } }).response;

    if (response === undefined) {
      return;
    }

    dispatchDeletionConstraintError(response.status, response.data);
  });
}

function extractPayloadJson(body: unknown): string | null {
  const parsed = typeof body === 'string' ? parseJson(body) : body;

  if (parsed === null || typeof parsed !== 'object') {
    return null;
  }

  const errors = (parsed as ValidationErrorResponseBody).errors;
  const raw = errors?.[VALIDATION_ERROR_KEYS.deletionConstraints];
  const payloadJson = Array.isArray(raw) ? raw[0] : raw;

  return typeof payloadJson === 'string' && payloadJson !== '' ? payloadJson : null;
}
